/**
 * syncDemoHistorialFromCierres.ts — Aplana snapshots de cierres demo a ventas/movimientos LS.
 *
 * Conecta Historial de Ventas y Movimientos con la jornada multicaja (240 ventas + movs, 4 turnos incl. madrugada).
 * Preserva registros del usuario que no sean IDs demo.
 */

import { readCreditosRawConLegacy } from "@/features/caja/utils/readCreditoFromStorage";
import {
  runTransaction,
  txRead,
  txReadRaw,
  txWrite,
  txWriteRaw,
} from "@/lib/persistence/transactionalStore";
import type { CierreCaja, MovimientoCaja, Venta } from "../types/caja.types";
import { isTicketDemoVenta } from "../fixtures/ticketRegressionCart";
import { collectVentasFromCierres } from "../utils/jornadaCierresHelpers";
import { MAY12_DEMO_SEED_VERSION } from "./may12CajaDemoSeed.data";
import { getMulticajaDemoCierreIds } from "./multicajaJornadaDemo.data";
import {
  ANEXO_B_CIERRE_DEMO_ID,
  ANEXO_B_SESION_DEMO_ID,
} from "./anexoBCierreDemo.data";
import { localCalendarDayIso } from "./shiftCajaDemoMay12AnchorToLocalDay";
import { remapVentaProductosToCatalog } from "./demoInventoryProductMap";
import { syncDemoStockMovementsFromVentas } from "./syncDemoStockMovementsFromVentas";
import { mergeVentaPreservingAnulacion } from "../utils/applyAnulacionVenta";
import {
  hasUserRealCajaActivity,
  purgeDemoJornadaFromStorage,
} from "./demoJornadaLifecycle";
import {
  buildDemoShowcaseMovimientos,
  isDemoShowcaseCreditoId,
  isDemoShowcaseMovimientoId,
  isDemoShowcaseVentaId,
} from "./demoShowcaseHistorial.data";
import type { Credito } from "../types/caja.types";

const LS_HISTORIAL_SYNC_VERSION = "altenot_caja_demo_historial_sync";

export const DEMO_HISTORIAL_SYNC_VERSION = "historial-sync-v4";

const DEMO_CIERRE_IDS = new Set([
  ...getMulticajaDemoCierreIds(),
  ANEXO_B_CIERRE_DEMO_ID,
]);

/** True si faltan los movimientos showcase en historial (post-actualización v24). */
export function demoShowcaseMissingFromHistorial(): boolean {
  const list = txRead<MovimientoCaja[] | null>("caja_movimientos", null);
  if (!Array.isArray(list)) return true;
  const expected = buildDemoShowcaseMovimientos().map((m) => m.id);
  return !expected.every((id) => list.some((m) => m.id === id));
}

/** True si hay cierres demo pero ventas demo vacías o con fecha distinta al día local. */
export function demoJornadaVentasMissingFromHistorial(): boolean {
  const cierres = readCierresHistorial();
  const demoCierres = cierres.filter((c) => DEMO_CIERRE_IDS.has(c.id));
  if (demoCierres.length === 0) return true;

  const ventas = readVentasHistorial();
  const demoVentas = ventas.filter((v) => isDemoVentaId(v.id));
  if (demoVentas.length === 0) return true;

  const today = localCalendarDayIso();
  return !demoVentas.some((v) => (v.fecha || "").startsWith(today));
}

export function isDemoVentaId(id: string): boolean {
  return (
    id.startsWith("v-pc") ||
    id.startsWith("may12-") ||
    id.startsWith("anexo-b-") ||
    id.startsWith("venta_demo_") ||
    id.startsWith("177859575") || // Cierres/ventas del seed canónico
    isDemoShowcaseVentaId(id)
  );
}

function isAnexoBDemoSession(sesionId: string | undefined): boolean {
  return sesionId === ANEXO_B_SESION_DEMO_ID;
}

export function isDemoMovimientoId(id: string): boolean {
  return (
    id.startsWith("m-gasto-") ||
    id.startsWith("m-ingreso-") ||
    id.startsWith("m-retiro-") ||
    id.startsWith("may12-") ||
    id.startsWith("anexo-b-") ||
    id.startsWith("177859575") ||
    isDemoShowcaseMovimientoId(id)
  );
}

function isDemoCreditoId(id: string): boolean {
  return (
    id.startsWith("may12-f-") ||
    id.startsWith("anexo-b-f-") ||
    isDemoShowcaseCreditoId(id)
  );
}

export { isDemoCreditoId };

export function collectCreditosFromCierres(cierres: CierreCaja[]): Credito[] {
  const seen = new Set<string>();
  const out: Credito[] = [];
  for (const cierre of cierres) {
    for (const f of cierre.creditosSnapshot ?? []) {
      if (seen.has(f.id)) continue;
      seen.add(f.id);
      out.push(f);
    }
  }
  return out.sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
  );
}

function isDemoCierre(cierre: CierreCaja): boolean {
  return DEMO_CIERRE_IDS.has(cierre.id);
}

export function collectMovimientosFromCierres(
  cierres: CierreCaja[],
): MovimientoCaja[] {
  const seen = new Set<string>();
  const out: MovimientoCaja[] = [];
  for (const cierre of cierres) {
    for (const m of cierre.movimientosSnapshot ?? []) {
      if (m.estado === "anulado" || seen.has(m.id)) continue;
      seen.add(m.id);
      out.push({
        ...m,
        puntoCajaId: m.puntoCajaId ?? cierre.puntoCajaId,
        puntoCajaNombre:
          m.puntoCajaNombre?.trim() ||
          cierre.puntoCajaNombre?.trim() ||
          undefined,
      });
    }
  }
  return out.sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
  );
}

function readCierresHistorial(): CierreCaja[] {
  const parsed = txRead<CierreCaja[] | null>("caja_cierres_historial", null);
  return Array.isArray(parsed) ? parsed : [];
}

function readVentasHistorial(): Venta[] {
  const parsed = txRead<Venta[] | null>("caja_ventas_historial", null);
  return Array.isArray(parsed) ? parsed : [];
}

function readMovimientosHistorial(): MovimientoCaja[] {
  const parsed = txRead<MovimientoCaja[] | null>("caja_movimientos", null);
  return Array.isArray(parsed) ? parsed : [];
}

function readCreditosHistorial(): Credito[] {
  const parsed = readCreditosRawConLegacy();
  return Array.isArray(parsed) ? parsed : [];
}

function syncStorageKey(): string {
  return `${DEMO_HISTORIAL_SYNC_VERSION}:${MAY12_DEMO_SEED_VERSION}:${localCalendarDayIso()}`;
}

/**
 * Escribe ventas y movimientos demo en LS a partir de cierres jornada.
 * @returns true si hubo escritura
 */
export function syncDemoHistorialFromCierres(
  allCierres: CierreCaja[],
  options?: { force?: boolean },
): boolean {
  if (hasUserRealCajaActivity()) {
    return purgeDemoJornadaFromStorage();
  }

  const demoCierres = allCierres.filter(isDemoCierre);
  if (demoCierres.length === 0) return false;

  const key = syncStorageKey();
  if (
    !options?.force &&
    !demoShowcaseMissingFromHistorial() &&
    !demoJornadaVentasMissingFromHistorial() &&
    txReadRaw(LS_HISTORIAL_SYNC_VERSION) === key
  ) {
    return false;
  }

  const existingById = new Map(
    readVentasHistorial().map((v) => [v.id, v] as const),
  );

  const demoVentas = collectVentasFromCierres(demoCierres)
    .map(remapVentaProductosToCatalog)
    .map((v) => mergeVentaPreservingAnulacion(v, existingById.get(v.id)))
    .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

  const demoMovs = collectMovimientosFromCierres(demoCierres);
  const demoCreditos = collectCreditosFromCierres(demoCierres);

  const userVentas = readVentasHistorial().filter(
    (v) =>
      !isDemoVentaId(v.id) &&
      !isTicketDemoVenta(v.id) &&
      !isAnexoBDemoSession(v.sesionId),
  );
  const userMovs = readMovimientosHistorial().filter(
    (m) => !isDemoMovimientoId(m.id) && !isAnexoBDemoSession(m.sesionId),
  );
  const userCreditos = readCreditosHistorial().filter(
    (f) => !isDemoCreditoId(f.id) && !isAnexoBDemoSession(f.sesionId),
  );

  // Escritura atómica de las 3 claves + marca de versión: nunca deben quedar
  // desincronizadas (ventas demo escritas pero movimientos no) ante un crash.
  runTransaction((): void => {
    txWrite("caja_ventas_historial", [...demoVentas, ...userVentas]);
    txWrite("caja_movimientos", [...demoMovs, ...userMovs]);
    txWrite("caja_creditos", [...demoCreditos, ...userCreditos]);
    txWriteRaw(LS_HISTORIAL_SYNC_VERSION, key);
  });

  syncDemoStockMovementsFromVentas(demoVentas, { force: true });

  try {
    window.dispatchEvent(new CustomEvent("caja-demo-historial-synced"));
  } catch {
    /* ignore */
  }

  return true;
}

/** Sincroniza desde el historial de cierres actual en LS. */
export function ensureDemoHistorialSynced(options?: {
  force?: boolean;
}): boolean {
  return syncDemoHistorialFromCierres(readCierresHistorial(), options);
}
